import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  IconButton,
  Link,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DownloadIcon from "@mui/icons-material/Download";
import UsageApi, {
  ConsolidatedAuditLogRow,
  ToolAuditLogEntryDetail,
  UsageCustomerOptionRow,
} from "../apis/usageApi";
import type { UsageQuickDatePresetId } from "../apis/usageApiUtils";
import {
  getUsageDateRangeForPreset,
  USAGE_QUICK_RANGE_OPTIONS,
} from "../apis/usageApiUtils";
import LayeredAutocompleteSelect from "../components/LayeredAutocompleteSelect";
import { useUserMsp } from "../hooks/useUserMsp";
import { canAccessUsageAnalytics, isHorizonRootRole } from "../utils/horizonRoles";
import {
  formatParamsAsKeyValue,
  getFullOutputString,
  isErrorRow,
  scrollSx,
} from "../utils/toolAuditLogUtils";

const cardSx = { borderRadius: "8px", border: "1px solid #E2E8F0" };

const AUDIT_DEFAULT_PRESET: UsageQuickDatePresetId = "last_30_days";
const auditInitialDateRange = getUsageDateRangeForPreset(AUDIT_DEFAULT_PRESET)!;

const QUICK_RANGE_SELECT_OPTIONS = USAGE_QUICK_RANGE_OPTIONS.map((o) => ({
  value: o.id,
  label: o.label,
}));

function permissionLabel(permission: string | null | undefined): string {
  if (!permission) return "—";
  const p = String(permission).toLowerCase();
  if (p === "auto") return "Auto";
  if (p === "approval") return "Approval";
  if (p === "off") return "Off";
  return permission;
}

function approvedByLabel(row: ConsolidatedAuditLogRow): string {
  const p = row.action_permission ? String(row.action_permission).toLowerCase() : "";
  if (p !== "approval") return "—";
  const e = row.user_email?.trim();
  return e || "—";
}

function formatWhen(iso: string | null | undefined): string {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  } catch {
    return iso;
  }
}

/** Supports legacy API rows before `context_type` was added. */
function normalizeContextType(row: ConsolidatedAuditLogRow): "ticket" | "assistant" {
  if (row.context_type === "ticket" || row.context_type === "assistant") {
    return row.context_type;
  }
  const tid = row.ticket_id?.trim();
  return tid ? "ticket" : "assistant";
}

function threadLinkLabel(row: ConsolidatedAuditLogRow): string {
  const name = row.thread_name?.trim();
  if (name) return name;
  if (row.ai_thread_id) return `Thread ${row.ai_thread_id}`;
  return "—";
}

/**
 * Ticket rows → Theo supervisor ticket (PSA-backed). Chat assistant → Chat Assistant deep link.
 * Links only when the viewer's MSP matches the filtered MSP (same rule as the Ticket column).
 */
function threadColumnHref(
  row: ConsolidatedAuditLogRow,
  ctx: "ticket" | "assistant",
  canOpenMspLinks: boolean
): string | null {
  if (!canOpenMspLinks) return null;
  const ticketStr = row.ticket_id?.trim() || "";
  const threadIdStr = row.ai_thread_id?.trim() || "";
  if (ctx === "ticket" && ticketStr) {
    return `/supervisor/tickets/${encodeURIComponent(ticketStr)}`;
  }
  if (threadIdStr) {
    return `/chat-assistant/${encodeURIComponent(threadIdStr)}`;
  }
  return null;
}

function escapeCsvCell(v: unknown): string {
  if (v == null || v === "") return "";
  const s = typeof v === "object" ? JSON.stringify(v) : String(v);
  return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

/** All rows currently loaded for the active filters (same cap as the table, e.g. 1000). */
function buildConsolidatedAuditLogsCsv(rows: ConsolidatedAuditLogRow[]): string {
  const headers = [
    "audit_entry_id",
    "start_timestamp",
    "context",
    "thread_name",
    "ai_thread_id",
    "ticket_id",
    "customer_name",
    "customer_id",
    "tool_name",
    "permission",
    "approved_by",
    "status",
  ];
  const lines = [headers.join(",")];
  for (const row of rows) {
    const ctx = normalizeContextType(row);
    lines.push(
      [
        escapeCsvCell(row.id),
        escapeCsvCell(row.start_timestamp),
        escapeCsvCell(ctx === "ticket" ? "ticket" : "assistant"),
        escapeCsvCell(row.thread_name ?? ""),
        escapeCsvCell(row.ai_thread_id ?? ""),
        escapeCsvCell(row.ticket_id ?? ""),
        escapeCsvCell(row.customer_name ?? ""),
        escapeCsvCell(row.customer_id ?? ""),
        escapeCsvCell(permissionLabel(row.action_permission)),
        escapeCsvCell(approvedByLabel(row)),
        escapeCsvCell(row.status ?? ""),
      ].join(",")
    );
  }
  return lines.join("\r\n");
}

function detailFromApiEntry(entry: ToolAuditLogEntryDetail) {
  const paramRecord =
    entry.param_json &&
    typeof entry.param_json === "object" &&
    !Array.isArray(entry.param_json)
      ? (entry.param_json as Record<string, unknown>)
      : null;
  const outputRecord =
    entry.output_json &&
    typeof entry.output_json === "object" &&
    !Array.isArray(entry.output_json)
      ? (entry.output_json as Record<string, unknown>)
      : null;

  const pseudoRow = {
    ...entry,
    param_json: paramRecord,
    output_json: outputRecord,
    status: entry.status ?? "",
  };
  const failed = isErrorRow(pseudoRow as any);

  let paramsStr = paramRecord ? formatParamsAsKeyValue(paramRecord) : "";
  if (!paramsStr && entry.param_json != null) {
    try {
      paramsStr = JSON.stringify(entry.param_json, null, 2);
    } catch {
      paramsStr = String(entry.param_json);
    }
  }

  let outputStr = "";
  if (failed) {
    outputStr = entry.error_message ?? "";
  } else if (outputRecord) {
    outputStr = getFullOutputString(outputRecord);
  } else if (entry.output_json != null) {
    try {
      outputStr = JSON.stringify(entry.output_json, null, 2);
    } catch {
      outputStr = String(entry.output_json);
    }
  }

  return {
    failed,
    paramsStr: paramsStr || "(none)",
    outputStr: outputStr || "(none)",
    reasoning: entry.reasoning?.trim() ?? "(none)",
  };
}

const AuditLogs: React.FC = () => {
  const { userMspInfo, isLoading: userMspLoading, error: userMspError } = useUserMsp();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [rows, setRows] = useState<ConsolidatedAuditLogRow[]>([]);

  const [quickPreset, setQuickPreset] = useState<UsageQuickDatePresetId>(AUDIT_DEFAULT_PRESET);
  const [startDate, setStartDate] = useState<string>(auditInitialDateRange.startDate);
  const [endDate, setEndDate] = useState<string>(auditInitialDateRange.endDate);
  const [ticketFilter, setTicketFilter] = useState<string>("");
  const [customerId, setCustomerId] = useState<string>("");
  const [customerOptions, setCustomerOptions] = useState<UsageCustomerOptionRow[]>([]);
  /** ROOT only: when true (default), audit listing omits actions attributed to ROOT users. */
  const [excludeRootUsers, setExcludeRootUsers] = useState(true);

  const [actionDetailOpen, setActionDetailOpen] = useState(false);
  const [actionDetail, setActionDetail] = useState<ToolAuditLogEntryDetail | null>(null);
  const [actionDetailLoading, setActionDetailLoading] = useState(false);
  const [actionDetailError, setActionDetailError] = useState<string | null>(null);

  const canView = canAccessUsageAnalytics(userMspInfo?.role);
  const isRootViewer = isHorizonRootRole(userMspInfo?.role);

  const usageMspIdForRequest = useMemo(() => {
    if (!canView) return null;
    return userMspInfo?.msp_id != null ? Number(userMspInfo.msp_id) : null;
  }, [canView, userMspInfo?.msp_id]);

  const canLinkTicketIds = useMemo(() => {
    return userMspInfo?.msp_id != null;
  }, [userMspInfo?.msp_id]);

  const loadCustomers = useCallback(async () => {
    if (!canView || usageMspIdForRequest == null) return;
    try {
      const list = await UsageApi.getUsageCustomerOptions({
        mspId: null,
      });
      setCustomerOptions(Array.isArray(list) ? list : []);
    } catch {
      setCustomerOptions([]);
    }
  }, [canView, usageMspIdForRequest]);

  useEffect(() => {
    setCustomerId("");
  }, [usageMspIdForRequest]);

  useEffect(() => {
    if (!userMspLoading && canView) {
      void loadCustomers();
    }
  }, [userMspLoading, canView, loadCustomers]);

  const loadAuditLogs = useCallback(async () => {
    if (!canView || usageMspIdForRequest == null) return;
    try {
      setLoading(true);
      setError(null);
      const tid = ticketFilter.trim();
      const cid =
        customerId.trim() === "" ? null : Number.parseInt(customerId.trim(), 10);
      const response = await UsageApi.getConsolidatedAuditLogs({
        startDate: startDate || null,
        endDate: endDate || null,
        customerId: cid != null && Number.isFinite(cid) && cid > 0 ? cid : null,
        ticketId: tid || null,
        limit: 1000,
        includeRootUsers: isRootViewer && !excludeRootUsers,
        mspId: null,
      });
      setRows(Array.isArray(response.rows) ? response.rows : []);
    } catch (err: any) {
      setError(err?.userMessage || err?.message || "Failed to load audit logs");
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [
    canView,
    usageMspIdForRequest,
    startDate,
    endDate,
    ticketFilter,
    customerId,
    isRootViewer,
    excludeRootUsers,
  ]);

  useEffect(() => {
    if (userMspLoading || !canView) return;
    if (usageMspIdForRequest == null) return;
    void loadAuditLogs();
  }, [userMspLoading, canView, usageMspIdForRequest, loadAuditLogs]);

  useEffect(() => {
    document.title = "Audit Logs - Theo";
    return () => {
      document.title = "Theo";
    };
  }, []);

  const openActionDetail = useCallback(
    async (entryId: string) => {
      if (!usageMspIdForRequest || !entryId) return;
      setActionDetailOpen(true);
      setActionDetailLoading(true);
      setActionDetail(null);
      setActionDetailError(null);
      try {
        const detail = await UsageApi.getToolAuditLogEntry(entryId, {
          mspId: null,
        });
        setActionDetail(detail);
      } catch (err: any) {
        setActionDetail(null);
        setActionDetailError(err?.userMessage || err?.message || "Failed to load action details");
      } finally {
        setActionDetailLoading(false);
      }
    },
    [usageMspIdForRequest]
  );

  const closeActionDetail = () => {
    setActionDetailOpen(false);
    setActionDetail(null);
    setActionDetailLoading(false);
    setActionDetailError(null);
  };

  const customerSelectOptions = useMemo(() => {
    const opts = [{ value: "", label: "All customers" }];
    for (const c of customerOptions) {
      if (c.id == null) continue;
      const label = (c.name && String(c.name).trim()) || `Customer ${c.id}`;
      opts.push({ value: String(c.id), label });
    }
    return opts;
  }, [customerOptions]);

  const actionDetailFormatted = actionDetail ? detailFromApiEntry(actionDetail) : null;

  const handleDownloadCsv = useCallback(() => {
    if (rows.length === 0) return;
    const csvBody = buildConsolidatedAuditLogsCsv(rows);
    const csvContent = `\uFEFF${csvBody}`;
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    const safeStart = (startDate || "start").replace(/[^\d-]/g, "") || "start";
    const safeEnd = (endDate || "end").replace(/[^\d-]/g, "") || "end";
    a.download = `theo-logs_${safeStart}_to_${safeEnd}_${rows.length}rows.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }, [rows, startDate, endDate]);

  if (userMspLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (userMspError || !userMspInfo) {
    return (
      <Box sx={{ p: { xs: 2, sm: 3, md: 4 }, width: "100%" }}>
        <Alert severity="error">
          {userMspError || "Unable to load your MSP profile. Try refreshing the page."}
        </Alert>
      </Box>
    );
  }

  if (!canView) {
    return (
      <Box sx={{ p: { xs: 2, sm: 3, md: 4 }, width: "100%" }}>
        <Alert severity="info">
          Audit Logs are only available to MSP administrators. This link is hidden from your sidebar.
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, sm: 3, md: 4 }, width: "100%" }}>
      <Typography variant="h5" sx={{ fontWeight: 700, mb: 2 }}>
        Audit Logs
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3, maxWidth: 800 }}>
        Every tool action Theo ran for your MSP (read and write), from the same data as the per-ticket audit
        log. Default range is the last 30 days; up to 1,000 most recent rows are shown.
      </Typography>

      {error && (
        <Alert sx={{ mb: 3 }} severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Card sx={{ ...cardSx, mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="flex-start">
            <Grid item xs={12} md={3}>
              <TextField
                label="Start Date"
                type="date"
                size="small"
                value={startDate}
                onChange={(event) => {
                  setQuickPreset("custom");
                  setStartDate(event.target.value);
                }}
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                label="End Date"
                type="date"
                size="small"
                value={endDate}
                onChange={(event) => {
                  setQuickPreset("custom");
                  setEndDate(event.target.value);
                }}
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <LayeredAutocompleteSelect
                label="Quick range"
                size="small"
                value={quickPreset}
                disableClearable
                options={QUICK_RANGE_SELECT_OPTIONS}
                onChange={(next) => {
                  const id = next as UsageQuickDatePresetId;
                  setQuickPreset(id);
                  const range = getUsageDateRangeForPreset(id);
                  if (range) {
                    setStartDate(range.startDate);
                    setEndDate(range.endDate);
                  }
                }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <LayeredAutocompleteSelect
                label="Customer"
                size="small"
                value={customerId}
                disableClearable
                options={customerSelectOptions}
                onChange={(next) => setCustomerId(String(next))}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                label="Ticket ID"
                size="small"
                value={ticketFilter}
                onChange={(e) => setTicketFilter(e.target.value)}
                placeholder="Exact ticket id"
                fullWidth
              />
            </Grid>
            {isRootViewer ? (
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={excludeRootUsers}
                      onChange={(e) => setExcludeRootUsers(e.target.checked)}
                      size="small"
                    />
                  }
                  label="Exclude root user actions"
                />
              </Grid>
            ) : null}
          </Grid>
        </CardContent>
      </Card>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Card sx={cardSx}>
          <CardContent sx={{ p: 0, "&:last-child": { pb: 0 } }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                alignItems: "center",
                flexWrap: "wrap",
                gap: 1,
                px: 2,
                pt: 2,
                pb: 1,
              }}
            >
              <Button
                variant="outlined"
                size="small"
                startIcon={<DownloadIcon />}
                disabled={rows.length === 0}
                onClick={handleDownloadCsv}
              >
                Download CSV ({rows.length} {rows.length === 1 ? "row" : "rows"})
              </Button>
            </Box>
            <TableContainer sx={{ maxHeight: 560 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ minWidth: 160 }}>When</TableCell>
                    <TableCell sx={{ minWidth: 110 }}>Context</TableCell>
                    <TableCell sx={{ minWidth: 140 }}>Thread</TableCell>
                    <TableCell sx={{ minWidth: 100 }}>Ticket</TableCell>
                    <TableCell sx={{ minWidth: 140 }}>Customer</TableCell>
                    <TableCell sx={{ minWidth: 200 }}>Action</TableCell>
                    <TableCell sx={{ minWidth: 100 }}>Permission</TableCell>
                    <TableCell sx={{ minWidth: 160 }}>Approved by</TableCell>
                    <TableCell sx={{ minWidth: 100 }}>Result</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((row) => {
                    const ticketStr = row.ticket_id?.trim() || "";
                    const ctx = normalizeContextType(row);
                    const customerDisplay =
                      row.customer_name?.trim() ||
                      (row.customer_id != null ? `ID ${row.customer_id}` : "—");
                    const ok = String(row.status || "").toUpperCase() === "SUCCESS";
                    const threadIdStr = row.ai_thread_id?.trim() || "";
                    const threadHref = threadColumnHref(row, ctx, canLinkTicketIds);
                    return (
                      <TableRow
                        key={row.id != null ? `audit-${row.id}` : `audit-fallback-${row.start_timestamp}-${row.tool_name}`}
                        hover
                      >
                        <TableCell sx={{ whiteSpace: "nowrap" }}>{formatWhen(row.start_timestamp)}</TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={ctx === "ticket" ? "Ticket" : "Chat Agent"}
                            color={ctx === "ticket" ? "primary" : "default"}
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell sx={{ wordBreak: "break-word" }}>
                          {threadHref ? (
                            <Link
                              href={threadHref}
                              target="_blank"
                              rel="noopener noreferrer"
                              underline="hover"
                              sx={{ fontWeight: 600 }}
                            >
                              {threadLinkLabel(row)}
                            </Link>
                          ) : threadIdStr || ticketStr ? (
                            threadLinkLabel(row)
                          ) : (
                            "—"
                          )}
                        </TableCell>
                        <TableCell sx={{ wordBreak: "break-word" }}>
                          {ticketStr ? (
                            canLinkTicketIds ? (
                              <Link
                                href={`/supervisor/tickets/${encodeURIComponent(ticketStr)}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                underline="hover"
                                sx={{ fontWeight: 600 }}
                              >
                                {ticketStr}
                              </Link>
                            ) : (
                              ticketStr
                            )
                          ) : (
                            "—"
                          )}
                        </TableCell>
                        <TableCell sx={{ wordBreak: "break-word" }}>{customerDisplay}</TableCell>
                        <TableCell sx={{ wordBreak: "break-word" }}>
                          {row.id ? (
                            <Link
                              component="button"
                              type="button"
                              onClick={() => void openActionDetail(row.id as string)}
                              underline="hover"
                              sx={{ fontWeight: 600, cursor: "pointer", textAlign: "left" }}
                            >
                              {row.tool_name || "—"}
                            </Link>
                          ) : (
                            row.tool_name || "—"
                          )}
                        </TableCell>
                        <TableCell>{permissionLabel(row.action_permission)}</TableCell>
                        <TableCell sx={{ wordBreak: "break-word" }}>{approvedByLabel(row)}</TableCell>
                        <TableCell>
                          {row.status ? (
                            <Chip
                              size="small"
                              label={row.status}
                              color={ok ? "success" : "error"}
                              variant="filled"
                            />
                          ) : (
                            "—"
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  {rows.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={9} align="center">
                        No actions in this range for the selected filters.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}

      <Dialog
        open={actionDetailOpen}
        onClose={closeActionDetail}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            maxHeight: "90vh",
          },
        }}
      >
        <DialogTitle
          sx={{
            fontFamily: "var(--theo-font-sans)",
            fontWeight: 600,
            borderBottom: "1px solid #ECECEC",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          Action details
          <IconButton aria-label="Close" size="small" onClick={closeActionDetail}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent
          sx={{
            p: 0,
            backgroundColor: "#1a1a1a",
            color: "#e6edf3",
            fontFamily: "ui-monospace, monospace",
            fontSize: "12px",
          }}
        >
          {actionDetailLoading && (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress size={32} sx={{ color: "#666F8D" }} />
            </Box>
          )}
          {!actionDetailLoading && actionDetailError && (
            <Box sx={{ p: 2, bgcolor: "background.paper" }}>
              <Alert severity="error">{actionDetailError}</Alert>
            </Box>
          )}
          {!actionDetailLoading && actionDetail && actionDetailFormatted && (
            <Box sx={{ px: 2, pt: 1, pb: 2 }}>
              <Box sx={{ mb: 1.5 }}>
                <Typography component="span" sx={{ color: "#888", display: "block", mb: 0.5 }}>
                  Tool name
                </Typography>
                <Box component="pre" sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word", m: 0 }}>
                  {actionDetail.tool_name}
                </Box>
              </Box>
              <Box sx={{ mb: 1.5 }}>
                <Typography component="span" sx={{ color: "#888", display: "block", mb: 0.5 }}>
                  Reasoning
                </Typography>
                <Box component="pre" sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word", m: 0 }}>
                  {actionDetailFormatted.reasoning}
                </Box>
              </Box>
              <Box sx={{ mb: 1.5, ...scrollSx }}>
                <Typography component="span" sx={{ color: "#888", display: "block", mb: 0.5 }}>
                  Parameters
                </Typography>
                <Box
                  component="pre"
                  sx={{
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                    maxHeight: 200,
                    overflow: "auto",
                    m: 0,
                  }}
                >
                  {actionDetailFormatted.paramsStr}
                </Box>
              </Box>
              <Box sx={{ ...scrollSx }}>
                <Typography component="span" sx={{ color: "#888", display: "block", mb: 0.5 }}>
                  Output
                </Typography>
                <Box
                  component="pre"
                  sx={{
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                    maxHeight: 280,
                    overflow: "auto",
                    m: 0,
                    color: actionDetailFormatted.failed ? "#f85149" : "#e6edf3",
                  }}
                >
                  {actionDetailFormatted.outputStr}
                </Box>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeActionDetail}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AuditLogs;
